import clsx from 'clsx';
import { throttle } from 'lodash';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';

import { useInboxEntriesSocketSubscriptions } from './useInboxEntriesSocketSubscriptions';

import {
  InboxEntry,
  InboxEntryType,
  StatusFilterEnum,
  View,
} from '../../../types';
import EmailThreadEntry from '../InboxEntry/EmailThreadEntry/EmailThreadEntry';
import FileEntry from '../InboxEntry/FileEntry/FileEntry';

type InboxEntriesProps = {
  inboxEntries: InboxEntry[];
  projectId: string;
  filter: StatusFilterEnum;
  view: View;
  sidebarRef: React.RefObject<HTMLDivElement>;
  children: ReactNode;
  isSearchActive: boolean;
};

const InboxEntries = ({
  inboxEntries,
  projectId,
  filter,
  view,
  sidebarRef,
  children,
  isSearchActive,
}: InboxEntriesProps) => {
  const firstEntryRef = useRef(null);
  const [showScrollUpPill, setShowScrollUpPill] = useState(false);
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  console.log('inboxEntries', inboxEntries);

  const handleClickScrollUp = useCallback(() => {
    setShowScrollUpPill(false);
    firstEntryRef.current.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleShowUpdateToast = useCallback(() => {
    setShowUpdateToast(true);

    // Set a timeout to hide the toast after 3 seconds
    const timeoutId = setTimeout(() => {
      setShowUpdateToast(false);
    }, 3000);

    // Clean up the timeout when the component unmounts or the state changes
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const handleNotifyUpdates = useCallback(() => {
    if (!firstEntryRef || !firstEntryRef.current || !sidebarRef.current) {
      return;
    }

    const scrollableRect = sidebarRef.current.getBoundingClientRect();
    const elementRect = firstEntryRef.current.getBoundingClientRect();

    const isFirstEntryVisible =
      elementRect.top >= scrollableRect.top &&
      elementRect.bottom <= scrollableRect.bottom;

    if (!isFirstEntryVisible) {
      setShowScrollUpPill(true);
    } else {
      handleShowUpdateToast();
    }
  }, [setShowScrollUpPill, sidebarRef, handleShowUpdateToast]);

  useInboxEntriesSocketSubscriptions({
    projectId,
    filter,
    view,
    onNotifyUpdates: handleNotifyUpdates,
    onShowUpdateToast: handleShowUpdateToast,
  });

  useEffect(() => {
    const observerCallback = throttle(
      (entries) => {
        // Process intersection entries
        const isFirstEntryVisible = entries[0].isIntersecting;

        if (isFirstEntryVisible) {
          setShowScrollUpPill(false);
        }
      },
      100, // Adjust the throttle duration as needed
      { trailing: true }
    );

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
    });

    if (firstEntryRef.current) {
      observer.observe(firstEntryRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [inboxEntries.length]);

  return (
    <React.Fragment>
      {showScrollUpPill && (
        <div className="tw-absolute tw-left-1/2 tw-transform tw--translate-x-1/2 tw-top-[10px]">
          {/* <div className=""> */}
          <Button
            className={clsx(
              'tw-flex tw-justify-center tw-items-center',
              'tw-h-[32px] tw-py-[6px] tw-px-[10px] tw-m-0',
              'tw-text-white  tw-bg-cfo-blue-600 tw-w-[170px]',
              'tw-leading-[20px]',
              '!tw-rounded-full'
            )}
            onClick={handleClickScrollUp}
          >
            <Icon
              icon={ICONS.arrow_up({ className: 'tw-fill-white' })}
              className="tw-text-[100px]"
            />
            <span className="tw-pl-[3px]">New Entries Available</span>
          </Button>
          {/* </div> */}
        </div>
      )}

      <div
        className={`tw-overflow-y-auto tw-scroll-styles ${
          isSearchActive ? 'tw-h-[calc(100%-30px)]' : 'tw-h-full'
        }`}
      >
        {inboxEntries.map((inboxEntry, index) => {
          return (
            <div
              className="ie"
              key={`ies_${inboxEntry.data.id}_${index}`}
              ref={index === 0 ? firstEntryRef : null}
            >
              {inboxEntry.type === InboxEntryType.THREAD ? (
                <EmailThreadEntry
                  key={`${inboxEntry.data.id}_${index}`}
                  inboxEntry={inboxEntry}
                />
              ) : (
                <FileEntry
                  key={`${inboxEntry.data.id}_${index}`}
                  inboxEntry={inboxEntry}
                />
              )}
            </div>
          );
        })}
        {children}
      </div>

      {/* {showUpdateToast && <TextToast label={'Inbox was successfully updated!'} />} */}
      {showUpdateToast && (
        <React.Fragment>
          <div className="tw-absolute tw-left-1/2 tw-transform tw--translate-x-1/2 tw-bottom-[32px]">
            {/* Use 'tw-flex tw-justify-between tw-items-center' if with close*/}
            <div
              className={clsx(
                'tw-flex tw-justify-center tw-text-white tw-rounded-lg tw-bg-cfo-black',
                'tw-h-[48px] tw-w-[203px] tw-p-[15px] tw-lead-[18px]',
                'tw-font-brandonTextMedium'
              )}
            >
              <span className="tw-text-[12px]">
                Inbox was automatically updated.
              </span>
              {/* <Button onClick={() => setShowUpdateToast(false)} className="tw-m-0 !tw-p-0">
                <i className="material-icons closeIcon tw-text-[16px] tw-text-white">close</i>
              </Button> */}
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default InboxEntries;
